import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { B13, B13Ink, B13TypeScale, tint12 } from "../../../theme/b13Tokens";
import { primaryBtnSx } from "./moduleSx";

type Props = {
  onCreate: () => void;
};

// ── Local accent palette for the three onboarding steps ────────────
// Blue → Teal → Violet, tasteful and clearly sequential without turning
// the page into a rainbow. Uses solid pastel tints so the surfaces stay
// bright but readable and no gradient is introduced (spec: no gradients
// anywhere in Step Up styling passes).
const STEP_ACCENTS = [
  { bg: "#EAF2FE", border: "#C8DCFB", tile: "#1F6FEB", pillFg: "#173B84" }, // brand blue
  { bg: "#E6F4F3", border: "#B9DFDB", tile: "#0F766E", pillFg: "#0E4E48" }, // teal
  { bg: "#EEE9FB", border: "#D6CBF3", tile: "#6D4AC0", pillFg: "#3F2A78" }, // violet
] as const;

const STEP_ICONS = [
  <AddCircleOutlineOutlinedIcon key="add" />,
  <PublishOutlinedIcon key="publish" />,
  <AssignmentOutlinedIcon key="assignment" />,
] as const;

// A single onboarding step card. Kept internal — the layout of the
// step row (3-across on md+, stacked on mobile) is decided by the
// parent Stack so this card just fills its cell.
const StepCard: React.FC<{
  index: number;
  title: string;
  desc: string;
}> = ({ index, title, desc }) => {
  const a = STEP_ACCENTS[index] ?? STEP_ACCENTS[0];
  return (
    <Box
      sx={{
        position: "relative",
        flex: 1,
        minWidth: 0,
        p: { xs: 2, md: 2.5 },
        borderRadius: 2.5,
        bgcolor: a.bg,
        border: `1px solid ${a.border}`,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        transition: "box-shadow 160ms ease, transform 160ms ease",
        // Every step reads as clearly educational without heavy
        // elevation. On hover we lift very slightly so keyboard/mouse
        // users get a subtle affordance without the row jumping.
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: "0 8px 20px rgba(15,23,42,0.06)",
        },
      }}
    >
      {/* Numbered pill + step icon in one row so the eye lands on
          both cues at once. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: a.tile,
            color: "#FFFFFF",
            fontWeight: 800,
            fontSize: 13,
            letterSpacing: 0.3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 4px 10px ${a.tile}33`,
            flexShrink: 0,
          }}
        >
          {index + 1}
        </Box>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.5,
            bgcolor: "#FFFFFF",
            color: a.tile,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& svg": { fontSize: 20 },
            border: `1px solid ${a.border}`,
          }}
        >
          {STEP_ICONS[index]}
        </Box>
      </Box>
      <Typography sx={{ ...B13TypeScale.bodyStrong, color: a.pillFg, lineHeight: 1.2 }}>
        {title}
      </Typography>
      <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink2, lineHeight: 1.55 }}>
        {desc}
      </Typography>
    </Box>
  );
};

const CourseEmptyState: React.FC<Props> = ({ onCreate }) => {
  return (
    <Box
      sx={{
        // Outer onboarding surface. Solid soft blue tint so the card
        // reads as a bright, purposeful onboarding banner instead of
        // a blank white rectangle. No gradients per Step Up styling
        // convention.
        p: { xs: 3, md: 4 },
        borderRadius: 3,
        bgcolor: "#F4F8FF",
        border: `1px solid ${tint12(B13.primary)}`,
        boxShadow: "0 1px 2px rgba(15,23,42,0.03), 0 8px 24px rgba(31,111,235,0.06)",
      }}
    >
      {/* ── Hero: icon tile + title + supporting copy ──────────── */}
      <Box sx={{ maxWidth: 1080, mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: B13.primary,
              color: "#FFFFFF",
              boxShadow: "0 8px 20px rgba(31,111,235,0.25)",
              "& svg": { fontSize: 28 },
              flexShrink: 0,
            }}
          >
            <MenuBookOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                ...B13TypeScale.h2,
                fontSize: { xs: 20, md: 22 },
                letterSpacing: "-0.01em",
                lineHeight: 1.15,
              }}
            >
              Start your Course catalog
            </Typography>
            <Typography sx={{ ...B13TypeScale.caption, color: B13Ink.ink3, mt: 0.35 }}>
              Build your first course and make it ready for students and families.
            </Typography>
          </Box>
        </Box>

        {/* Description — same wording as before, wrapped to a
            comfortable measure so it doesn't stretch the width of a
            1600 px monitor. */}
        <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink2, mb: { xs: 2.5, md: 3 }, maxWidth: 720, lineHeight: 1.65 }}>
          A course is the definition of an offering the school teaches — its description,
          video, attachments, and the staff who runs it. Assignments belong to a course, so
          no assignment can be created until at least one course exists.
        </Typography>

        {/* ── Step cards. 3-across on md+, vertical stack on mobile.
            Each card owns its own colour identity (blue / teal /
            violet). A very light connector line runs behind the row
            on desktop to make the sequence obvious. ─────────────── */}
        <Box sx={{ position: "relative", mb: { xs: 2.5, md: 3 } }}>
          {/* Connector — hidden below md, sits behind the cards */}
          <Box
            aria-hidden
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              top: 42, // aligned with the numbered pill row
              left: "12%",
              right: "12%",
              height: 2,
              borderTop: `2px dashed ${tint12(B13.primary)}`,
              zIndex: 0,
            }}
          />
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 1.5, md: 2 }}
            sx={{ position: "relative", zIndex: 1 }}
          >
            <StepCard
              index={0}
              title="Create a course"
              desc="Give it a name, grade, subject, and assign a staff member. Add a description and any files families should see."
            />
            <StepCard
              index={1}
              title="Publish it"
              desc="Courses start as drafts. Set the status to Active when it is ready for families to see."
            />
            <StepCard
              index={2}
              title="Attach assignments"
              desc="Once at least one course exists, the Assignments module can be used — assignments are always attached to a course."
            />
          </Stack>
        </Box>

        {/* ── CTA footer. Sits inside the onboarding card instead of
            floating in the far corner, and pairs the primary action
            with a single reassuring line of copy. Wording only uses
            content the spec explicitly allowed us to introduce
            ("Ready to begin? …"). ───────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            p: { xs: 2, md: 2.25 },
            borderRadius: 2.5,
            bgcolor: "#FFFFFF",
            border: `1px solid ${tint12(B13.primary)}`,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ ...B13TypeScale.bodyStrong }}>
              Ready to begin?
            </Typography>
            <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink2, mt: 0.25 }}>
              Create your first course and start assigning learning content.
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon className="cta-arrow" />}
            onClick={onCreate}
            sx={{
              ...primaryBtnSx,
              px: 2.5,
              py: 1.15,
              fontSize: 14,
              boxShadow: "0 6px 14px rgba(31,111,235,0.28)",
              transition: "box-shadow 160ms ease, transform 160ms ease",
              "& .cta-arrow": {
                transition: "transform 160ms ease",
              },
              "&:hover": {
                bgcolor: B13.primary,
                filter: "brightness(0.95)",
                boxShadow: "0 10px 22px rgba(31,111,235,0.32)",
                "& .cta-arrow": { transform: "translateX(3px)" },
              },
              flexShrink: 0,
              alignSelf: { xs: "stretch", md: "auto" },
            }}
          >
            Create your first course
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CourseEmptyState;
